import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/atoms/card';
import { Skeleton } from '@/components/atoms/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/molecules/tabs';
import { ArrowUpRight, CreditCard, History } from 'lucide-react';
import { useOnboardingStatus } from '../hooks/use-onboarding-status';
import { usePayouts, useCancelPayout } from '../hooks/use-payouts';
import { useWalletBalance } from '@/features/wallet/hooks/use-wallet-balance';
import {
  OnboardingCard,
  PayoutRequestForm,
  PayoutMethodCard,
  PayoutsList,
  AddPayoutMethodForm,
} from '../components';

/**
 * Isole l'appel à `usePayouts`/`useCancelPayout` dans l'onglet Historique :
 * `TabsContent` ne monte son contenu que lorsque l'onglet est actif, donc la
 * requête ne part pas tant que le vendeur n'a pas ouvert « Historique ».
 */
function PayoutsHistoryTab() {
  const { data, isLoading, isError, refetch } = usePayouts();
  const cancelPayout = useCancelPayout();

  return (
    <PayoutsList
      payouts={data?.data ?? []}
      isLoading={isLoading}
      isError={isError}
      onRetry={refetch}
      onCancel={async (id) => {
        await cancelPayout.mutateAsync(id);
      }}
    />
  );
}

export function PayoutsContainer() {
  const { t } = useTranslation();
  const { data: onboardingStatus, isLoading: onboardingLoading } = useOnboardingStatus();
  const { data: balance, isLoading: balanceLoading } = useWalletBalance();

  const isLoading = onboardingLoading || balanceLoading;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32 w-full" />
        <div className="grid gap-6 md:grid-cols-2">
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const isOnboarded = onboardingStatus?.isOnboarded ?? false;
  const status = onboardingStatus?.status ?? 'NOT_STARTED';
  const payoutMethods = onboardingStatus?.payoutMethods ?? [];

  return (
    <div className="space-y-6">
      {/* Onboarding Status */}
      <OnboardingCard
        status={status}
        isOnboarded={isOnboarded}
        requirements={onboardingStatus?.requirements}
      />

      {/* Main Content */}
      {isOnboarded && (
        <Tabs defaultValue="request" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="request" className="flex items-center gap-2">
              <ArrowUpRight className="size-4" />
              {t('payouts.tabs.request')}
            </TabsTrigger>
            <TabsTrigger value="methods" className="flex items-center gap-2">
              <CreditCard className="size-4" />
              {t('payouts.tabs.methods')}
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2">
              <History className="size-4" />
              {t('payouts.tabs.history')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="request" className="space-y-6">
            <PayoutRequestForm
              availableBalance={balance?.available ?? 0}
              minimumAmount={balance?.minPayoutAmount}
              reserved={balance?.reserved}
              currency={balance?.currency ?? 'EUR'}
              payoutMethods={payoutMethods}
            />
          </TabsContent>

          <TabsContent value="methods" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="size-5" />
                  {t('payouts.methods.title')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {payoutMethods.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <CreditCard className="size-12 mx-auto mb-4 opacity-50" />
                    <p>{t('payouts.methods.empty')}</p>
                  </div>
                ) : (
                  payoutMethods.map((method) => (
                    <PayoutMethodCard key={method.id} method={method} />
                  ))
                )}
                <AddPayoutMethodForm />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history">
            <PayoutsHistoryTab />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
